import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

function Badge({ label, color }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: 700,
      background: `${color}18`, color,
      border: `1px solid ${color}30`,
    }}>{label}</span>
  );
}

function Avatar({ user, size = 36 }) {
  const url = user?.avatar;
  const name = user?.username || '?';
  if (url) return (
    <img src={url} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      onError={e => { e.target.style.display = 'none'; }} />
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, color: '#fff', fontSize: Math.max(11, size * 0.38),
    }}>{name[0]?.toUpperCase()}</div>
  );
}

function UserDetailModal({ user, onClose, onUpdated }) {
  const [form, setForm] = useState({ ...user });
  const [tab, setTab] = useState('info');
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${user.id}/`).then(r => setDetail(r.data)).catch(() => {});
  }, [user.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/`, form);
      onUpdated();
      onClose();
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return alert('Password must be ≥ 6 chars');
    setResetting(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password/`, { new_password: newPassword });
      alert('Password reset successfully. User tokens revoked.');
      setNewPassword('');
    } catch (e) {
      alert(e.response?.data?.error || 'Reset failed');
    } finally { setResetting(false); }
  };

  const togglePerm = async (field, val) => {
    try {
      const payload = { [field]: val };
      await api.put(`/admin/users/${user.id}/permissions/`, payload);
      setForm(f => ({ ...f, [field]: val }));
      onUpdated();
    } catch (e) {
      alert(e.response?.data?.error || 'Permission update failed');
    }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={S.modalHdr}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar user={user} size={44} />
            <div>
              <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14 }}>{user.username}</div>
              <div style={{ color: '#64748b', fontSize: 10 }}>User ID: {user.id}</div>
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={S.tabs}>
          {['info', 'permissions', 'password', 'posts', 'messages'].map(t => (
            <button key={t} style={{ ...S.tab, ...(tab === t ? S.activeTab : {}) }} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div style={S.modalBody}>
          {tab === 'info' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                ['Username', 'username'], ['Email', 'email'],
                ['First Name', 'first_name'], ['Last Name', 'last_name'],
                ['Bio', 'bio'], ['Mobile', 'mobile'],
                ['Country', 'country'], ['Gender', 'gender'],
              ].map(([label, field]) => (
                <label key={field} style={S.fieldWrap}>
                  <span style={S.fieldLabel}>{label}</span>
                  <input
                    style={S.input}
                    value={form[field] || ''}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={label}
                  />
                </label>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button style={S.saveBtn} onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>
          )}

          {tab === 'permissions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'is_staff', label: '🔑 Staff Access', desc: 'Can access admin panel' },
                { key: 'is_superuser', label: '👑 Superuser', desc: 'Full system access' },
                { key: 'is_active', label: '✅ Active Account', desc: 'User can log in' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={S.permRow}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 12 }}>{label}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{desc}</div>
                  </div>
                  <button
                    style={{ ...S.toggle, ...(form[key] ? S.toggleOn : S.toggleOff) }}
                    onClick={() => togglePerm(key, !form[key])}
                  >
                    {form[key] ? 'ON' : 'OFF'}
                  </button>
                </div>
              ))}
              <div style={{ ...S.infoBox, marginTop: 8 }}>
                ⚠️ Superuser can bypass all permission checks
              </div>
            </div>
          )}

          {tab === 'password' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={S.infoBox}>⚠️ This will invalidate user's current login session</div>
              <label style={S.fieldWrap}>
                <span style={S.fieldLabel}>New Password</span>
                <input
                  style={S.input} type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                />
              </label>
              <button style={S.dangerBtn} onClick={resetPassword} disabled={resetting}>
                {resetting ? 'Resetting...' : '🔄 Reset Password'}
              </button>
            </div>
          )}

          {tab === 'posts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!detail ? <div style={S.spinnerSm} /> :
               detail.posts?.length === 0 ? <p style={{ color: '#475569' }}>No posts</p> :
               detail.posts?.map(p => (
                 <div key={p.id} style={S.miniCard}>
                   {p.media_url && (
                     p.media_type?.startsWith('video/') ?
                       <video src={p.media_url} style={S.thumb} muted /> :
                       <img src={p.media_url} alt="post" style={S.thumb} onError={e => e.target.style.display = 'none'} />
                   )}
                   <div style={{ flex: 1 }}>
                     <div style={{ color: '#94a3b8', fontSize: 11 }}>{p.caption || <em style={{ color: '#475569' }}>No caption</em>}</div>
                     <div style={{ color: '#475569', fontSize: 10, marginTop: 4 }}>
                       {p.post_type} · ❤️ {p.like_count} · 💬 {p.comment_count} · {new Date(p.created_at).toLocaleDateString()}
                     </div>
                   </div>
                 </div>
               ))
              }
            </div>
          )}

          {tab === 'messages' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!detail ? <div style={S.spinnerSm} /> :
               detail.messages?.length === 0 ? <p style={{ color: '#475569' }}>No messages</p> :
               detail.messages?.map(m => (
                 <div key={m.id} style={S.msgRow}>
                   <div style={{ fontSize: 10, color: '#818cf8', fontWeight: 600 }}>
                     {m.sender_username} → {m.receiver_username}
                   </div>
                   <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                     {m.is_unsent ? <em style={{ color: '#475569' }}>Message unsent</em> : (m.content || (m.file_url ? '📎 File' : '—'))}
                   </div>
                   <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
                     {new Date(m.timestamp).toLocaleString()}
                     {m.is_read && ' · ✓✓ Read'}
                   </div>
                 </div>
               ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', email: '', password: '', is_staff: false, is_superuser: false, is_active: true });
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 25, search });
      const res = await api.get(`/admin/users/?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const deleteUser = async (user) => {
    try {
      await api.delete(`/admin/users/${user.id}/`);
      setConfirmDelete(null);
      fetchUsers();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  };

  const createUser = async () => {
    if (!createForm.username || !createForm.password) return alert('Username and password required');
    setCreating(true);
    try {
      await api.post('/admin/users/', createForm);
      setShowCreate(false);
      setCreateForm({ username: '', email: '', password: '', is_staff: false, is_superuser: false, is_active: true });
      fetchUsers();
    } catch (e) {
      alert(e.response?.data?.error || 'Create failed');
    } finally { setCreating(false); }
  };

  const totalPages = Math.ceil(total / 25);

  return (
    <div style={S.root}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>👥 Users</h1>
          <p style={S.subtitle}>{total} total users</p>
        </div>
        <button style={S.createBtn} onClick={() => setShowCreate(true)}>+ Create User</button>
      </div>

      {/* Search */}
      <div style={S.searchWrap}>
        <input
          style={S.searchInput}
          placeholder="Search by username or email..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        {loading ? (
          <div style={S.center}><div style={S.spinner} /></div>
        ) : users.length === 0 ? (
          <div style={S.center}><p style={{ color: '#475569' }}>No users found</p></div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr style={S.th}>
                <th style={S.thCell}>User</th>
                <th style={S.thCell}>Email</th>
                <th style={S.thCell}>Role</th>
                <th style={S.thCell}>Status</th>
                <th style={S.thCell}>Msgs</th>
                <th style={S.thCell}>Posts</th>
                <th style={S.thCell}>Joined</th>
                <th style={S.thCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={S.tr}>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ position: 'relative' }}>
                        <Avatar user={u} size={36} />
                        <div style={{
                          position: 'absolute', bottom: 0, right: 0,
                          width: 10, height: 10, borderRadius: '50%',
                          background: u.is_online ? '#34d399' : '#334155',
                          border: '2px solid #07070d',
                        }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 11 }}>{u.username}</div>
                        <div style={{ fontSize: 10, color: '#475569' }}>ID: {u.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...S.td, color: '#64748b', fontSize: 10 }}>{u.email || '—'}</td>
                  <td style={S.td}>
                    {u.is_superuser ? <Badge label="Superuser" color="#f59e0b" /> :
                     u.is_staff ? <Badge label="Staff" color="#6366f1" /> :
                     u.is_guest ? <Badge label="Guest" color="#64748b" /> :
                     <Badge label="User" color="#10b981" />}
                  </td>
                  <td style={S.td}>
                    {u.is_active ? (
                      u.is_online ? <Badge label="Online" color="#34d399" /> : <Badge label="Active" color="#475569" />
                    ) : <Badge label="Inactive" color="#f43f5e" />}
                  </td>
                  <td style={{ ...S.td, color: '#94a3b8', fontSize: 11 }}>{u.message_count}</td>
                  <td style={{ ...S.td, color: '#94a3b8', fontSize: 11 }}>{u.post_count}</td>
                  <td style={{ ...S.td, color: '#475569', fontSize: 10 }}>
                    {new Date(u.date_joined).toLocaleDateString()}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.actionBtn} onClick={() => setSelectedUser(u)} title="Edit">✏️</button>
                      <button style={S.dangerActionBtn} onClick={() => setConfirmDelete(u)} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={S.pagination}>
          <button style={S.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ color: '#64748b', fontSize: 11 }}>Page {page} of {totalPages}</span>
          <button style={S.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreate && (
        <div style={S.modalOverlay} onClick={() => setShowCreate(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14 }}>Create New User</span>
              <button style={S.closeBtn} onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {[['Username *', 'username', 'text'], ['Email', 'email', 'email'], ['Password *', 'password', 'password']].map(([label, field, type]) => (
                <label key={field} style={{ ...S.fieldWrap, marginBottom: 12 }}>
                  <span style={S.fieldLabel}>{label}</span>
                  <input style={S.input} type={type} value={createForm[field]} onChange={e => setCreateForm(f => ({ ...f, [field]: e.target.value }))} placeholder={label} />
                </label>
              ))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                {[['is_staff', 'Staff Member'], ['is_superuser', 'Superuser'], ['is_active', 'Active Account']].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={createForm[key]} onChange={e => setCreateForm(f => ({ ...f, [key]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
                  </label>
                ))}
              </div>
              <button style={{ ...S.saveBtn, marginTop: 16 }} onClick={createUser} disabled={creating}>
                {creating ? 'Creating...' : '+ Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div style={S.modalOverlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#f43f5e', fontSize: 14 }}>⚠️ Delete User</span>
              <button style={S.closeBtn} onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              <p style={{ color: '#94a3b8', margin: '0 0 16px' }}>
                Are you sure you want to permanently delete <strong style={{ color: '#e2e8f0' }}>{confirmDelete.username}</strong>?
                This will also delete all their messages and posts.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button style={S.dangerBtn} onClick={() => deleteUser(confirmDelete)}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdated={fetchUsers}
        />
      )}
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', padding: 24, background: '#07070d', display: 'flex', flexDirection: 'column', gap: 16, animation: 'fadeIn 0.3s ease' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: '#e2e8f0' },
  subtitle: { margin: '4px 0 0', color: '#475569', fontSize: 11 },
  createBtn: { padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  searchWrap: {},
  searchInput: { width: '100%', maxWidth: 400, padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  tableWrap: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'auto', flex: 1 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 },
  spinner: { width: 28, height: 28, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  th: { borderBottom: '1px solid rgba(255,255,255,0.06)' },
  thCell: { padding: '12px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' },
  td: { padding: '12px 16px', verticalAlign: 'middle' },
  actionBtn: { background: 'rgba(99,102,241,0.12)', border: 'none', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12 },
  dangerActionBtn: { background: 'rgba(244,63,94,0.1)', border: 'none', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 16 },
  pageBtn: { padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#94a3b8', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  // Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
  modalHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 },
  tabs: { display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 20px' },
  tab: { padding: '10px 14px', border: 'none', background: 'none', color: '#475569', cursor: 'pointer', fontSize: 11, fontWeight: 500, borderBottom: '2px solid transparent' },
  activeTab: { color: '#818cf8', borderBottomColor: '#6366f1' },
  modalBody: { padding: 20, overflowY: 'auto', flex: 1 },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 10, color: '#64748b', fontWeight: 600 },
  input: { padding: '9px 13px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none' },
  saveBtn: { padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  dangerBtn: { flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: 'rgba(244,63,94,0.15)', color: '#f43f5e', fontWeight: 700, cursor: 'pointer', fontSize: 12, border: '1px solid rgba(244,63,94,0.25)' },
  cancelBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  permRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  toggle: { padding: '6px 16px', borderRadius: 20, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 10 },
  toggleOn: { background: 'rgba(99,102,241,0.2)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' },
  toggleOff: { background: 'rgba(255,255,255,0.05)', color: '#475569', border: '1px solid rgba(255,255,255,0.08)' },
  infoBox: { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '10px 14px', color: '#fbbf24', fontSize: 11 },
  miniCard: { display: 'flex', gap: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12, alignItems: 'flex-start' },
  thumb: { width: 64, height: 64, borderRadius: 8, objectFit: 'cover', flexShrink: 0, background: '#1a1a2e' },
  msgRow: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, padding: '10px 12px' },
  spinnerSm: { width: 20, height: 20, border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '20px auto' },
};
